// BonusScreen.tsx
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  MdCardGiftcard,
  MdInfoOutline,
  MdStars,
  MdCalculate,
} from "react-icons/md";
import { useTheme } from "../../common/hooks/useTheme";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";
import { MobileHeader } from "../../common/components/mobileHeader/MobileHeader";
import { ProductHeader } from "../../common/components/productHeader/ProductHeader";
import { EmptyState } from "../../common/components/emptyState/EmptyState";
import styles from "./BonusScreen.module.scss";
import { PageMeta } from "../../common/components/pageMeta/PageMeta";

export default function BonusScreen() {
  const { theme } = useTheme();
  const currentCluster = useSelector(selectCurrentCluster);

  const bonusRules = useMemo(() => {
    if (!currentCluster?.bonuses?.length) return [];
    return [...currentCluster.bonuses]
      .map((rule) => ({
        level: Number(rule.level),
        value: Number(rule.value),
      }))
      .sort((a, b) => a.level - b.level);
  }, [currentCluster]);

  const getLevelName = (index: number, totalLevels: number) => {
    const isLast = index === totalLevels - 1;
    const baseName = `Уровень ${index + 1}`;
    return isLast ? `${baseName} (максимальный)` : baseName;
  };

  const subtitle = currentCluster
    ? `до ${currentCluster?.writeOff || 50}% кешбэка`
    : undefined;

  if (!bonusRules.length) {
    return (
      <div className={styles.container} style={{ backgroundColor: theme.background }}>
        <PageMeta />
        <div className={styles.mobileOnly}>
          <MobileHeader title="Бонусы" />
        </div>
        <div className={styles.desktopOnly}>
          <ProductHeader
            title="Бонусы"
            isFavorite={false}
            showFavorite={false}
            textColor={theme.text}
            borderColor={theme.border}
          />
        </div>
        <div className={styles.emptyWrapper}>
          <EmptyState
            icon={MdCardGiftcard}
            title="Бонусная система не активна"
            description="Бонусная программа пока не настроена"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} style={{ backgroundColor: theme.background }}>
      <PageMeta />
      <div className={styles.mobileOnly}>
        <MobileHeader title="Бонусная программа" subtitle={subtitle} />
      </div>
      <div className={styles.desktopOnly}>
        <ProductHeader
          title="Бонусная программа"
          isFavorite={false}
          showFavorite={false}
          textColor={theme.text}
          borderColor={theme.border}
        />
      </div>

      <div className={styles.scrollView}>
        <div className={styles.content}>
          <div className={styles.infoCard} style={{ backgroundColor: theme.card, borderColor: theme.border }}>
            <div className={styles.infoRow}>
              <MdInfoOutline size={20} color={theme.primary} className={styles.infoIcon} />
              <p className={styles.infoText} style={{ color: theme.text }}>
                За каждую покупку вы получаете бонусы, которыми можно оплатить до{" "}
                {currentCluster?.writeOff || 50}% следующих заказов
              </p>
            </div>
            <div className={styles.infoRow}>
              <MdStars size={20} color={theme.primary} className={styles.infoIcon} />
              <p className={styles.infoText} style={{ color: theme.text }}>
                Чем больше сумма покупок, тем выше ваш уровень и больше процент начисления
              </p>
            </div>
          </div>

          <h2 className={styles.sectionTitle} style={{ color: theme.text, marginTop: 24 }}>
            Уровни и начисления
          </h2>

          <div className={styles.levelsGrid}>
            {bonusRules.map((rule, index) => (
              <div
                key={index}
                className={styles.levelCard}
                style={{ backgroundColor: theme.card, borderColor: theme.border }}
              >
                <div className={styles.levelIcon} style={{ backgroundColor: `${theme.primary}20` }}>
                  <span className={styles.levelIconText} style={{ color: theme.primary }}>
                    {index + 1}
                  </span>
                </div>
                <div className={styles.levelInfo}>
                  <h3 className={styles.levelName} style={{ color: theme.text }}>
                    {getLevelName(index, bonusRules.length)}
                  </h3>
                  <p className={styles.levelDescription} style={{ color: theme.textSecondary }}>
                    от {rule.level.toLocaleString("ru-RU")} ₽
                  </p>
                  <p className={styles.levelPercent} style={{ color: theme.primary }}>
                    {rule.value}% бонусами
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.infoCard} style={{ backgroundColor: theme.card, borderColor: theme.border }}>
            <h3 className={styles.sectionTitle} style={{ color: theme.text, marginBottom: 12 }}>
              Примеры начисления
            </h3>
            {bonusRules.slice(0, 3).map((rule, index) => (
              <div
                key={index}
                className={styles.infoRow}
                style={{
                  borderBottom: index < 2 ? `1px solid ${theme.border}` : "none",
                  paddingBottom: index < 2 ? 12 : 0,
                  marginBottom: index < 2 ? 12 : 0,
                }}
              >
                <MdCalculate size={20} color={theme.primary} className={styles.infoIcon} />
                <p className={styles.infoText} style={{ color: theme.text }}>
                  {getLevelName(index, bonusRules.length)}: заказ 1000 ₽ ={" "}
                  {Math.floor(1000 * (rule.value / 100))} бонусов
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}